import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AppAuthService } from '@services/core/auth/app-auth.service';
import { AlertService } from '@services/helpers/alert/alert.service';
import { ButtonComponent } from '@shared/components/buttons/button/button.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import {
  SegmentedToggleComponent,
  SegmentedToggleOption,
} from '@shared/components/segmented-toggle/segmented-toggle.component';
import { RichDescriptionEditorComponent } from '@shared/components/rich-description-editor/rich-description-editor.component';
import {
  CreateSupportCaseDto,
  SupportCaseCategory,
  SupportCasePriority,
  SupportCaseSource,
  SupportCasesApiService,
  SupportCaseAttachment,
} from '@app/areas/crm/support-cases/services/support-cases-api.service';

@Component({
  selector: 'app-feedback-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, IconComponent, SegmentedToggleComponent, RichDescriptionEditorComponent],
  templateUrl: './feedback-form.component.html',
})
export class FeedbackFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(SupportCasesApiService);
  private readonly auth = inject(AppAuthService);
  private readonly alert = inject(AlertService);

  @ViewChild('descriptionTextarea') descriptionTextarea?: ElementRef<HTMLTextAreaElement>;

  @Input() allowMarkdownDnd = false;
  @Output() submitted = new EventEmitter<void>();

  readonly selectedFiles = signal<File[]>([]);
  readonly isDraggingOverDescription = signal(false);
  readonly localFileMap = new Map<string, string>();

  get angularEndpoint(): string {
    return this.api.angularEndpoint;
  }

  readonly form = this.fb.nonNullable.group({
    type: this.fb.nonNullable.control<SupportCaseCategory>(SupportCaseCategory.SUPPORT, [Validators.required]),
    title: this.fb.nonNullable.control('', [Validators.required]),
    description: this.fb.nonNullable.control('', [Validators.required]),
  });

  loading = false;

  readonly typeOptions: SegmentedToggleOption[] = [
    { value: `${SupportCaseCategory.BUG}`, label: 'Bug', icon: 'mdi-bug-outline', iconSize: '3', activeClass: 'segmented-toggle__item--tone-bug' },
    { value: `${SupportCaseCategory.SUPPORT}`, label: 'General support', icon: 'mdi-lifebuoy', iconSize: '3', activeClass: 'segmented-toggle__item--tone-support' },
    { value: `${SupportCaseCategory.FEATURE_REQUEST}`, label: 'Feature request', icon: 'mdi-lightbulb-outline', iconSize: '3', activeClass: 'segmented-toggle__item--tone-feature' },
  ];

  onDescriptionRichTextChanged(html: string): void {
    if (this.form.controls.description.value !== html) {
      this.form.controls.description.setValue(html);
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const filesArray = Array.from(input.files);
    this.addValidFiles(filesArray);
    input.value = '';
  }

  onDescriptionDragOver(event: DragEvent): void {
    if (!this.allowMarkdownDnd) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDescription.set(true);
  }

  onDescriptionDragLeave(event: DragEvent): void {
    if (!this.allowMarkdownDnd) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDescription.set(false);
  }

  onDescriptionDrop(event: DragEvent): void {
    if (!this.allowMarkdownDnd) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDescription.set(false);

    if (event.dataTransfer?.files?.length) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      const startIndex = this.selectedFiles().length;
      const validFiles = this.addValidFiles(droppedFiles);

      let tokenString = '';
      validFiles.forEach((file, idx) => {
        const tempId = `file-${startIndex + idx}`;
        const isImage = file.type?.startsWith('image/');
        tokenString += isImage
          ? `\n![${file.name}](attachment:${tempId})\n`
          : `\n[📎 ${file.name}](attachment:${tempId})\n`;
      });

      if (tokenString) {
        const currentDesc = this.form.controls.description.value || '';
        this.form.controls.description.setValue(`${currentDesc}${tokenString}`);
        this.form.controls.description.markAsDirty();
      }
    }
  }

  private addValidFiles(filesArray: File[]): File[] {
    const oversized = filesArray.filter(f => f.size > 25 * 1024 * 1024);
    if (oversized.length > 0) {
      this.alert.error('Some files exceed the 25MB limit and were skipped.');
    }

    const validFiles = filesArray.filter(f => f.size <= 25 * 1024 * 1024);
    const startIndex = this.selectedFiles().length;

    validFiles.forEach((file, idx) => {
      const fileIdx = startIndex + idx;
      const tempId = `file-${fileIdx}`;
      const blobUrl = URL.createObjectURL(file);
      this.localFileMap.set(tempId, blobUrl);
    });

    this.selectedFiles.update(current => [...current, ...validFiles]);
    return validFiles;
  }

  onFileDropFromEditor(file: File): void {
    if (file.size > 25 * 1024 * 1024) {
      this.alert.error('File size exceeds 25MB limit.');
      return;
    }

    const fileIndex = this.selectedFiles().length;
    const tempId = `file-${fileIndex}`;
    const blobUrl = URL.createObjectURL(file);
    this.localFileMap.set(tempId, blobUrl);

    this.selectedFiles.update(current => [...current, file]);

    const isImage = file.type?.startsWith('image/');
    const token = isImage
      ? `\n![${file.name}](attachment:${tempId})\n`
      : `\n[📎 ${file.name}](attachment:${tempId})\n`;

    const currentDesc = this.form.controls.description.value || '';
    this.form.controls.description.setValue(`${currentDesc}${token}`);
    this.form.controls.description.markAsDirty();
  }

  insertReferenceInDescription(file: File, fileIndex: number): void {
    const tempId = `file-${fileIndex}`;
    const isImage = file.type?.startsWith('image/');
    const token = isImage
      ? `\n![${file.name}](attachment:${tempId})\n`
      : `\n[📎 ${file.name}](attachment:${tempId})\n`;

    const currentDesc = this.form.controls.description.value || '';
    this.form.controls.description.setValue(`${currentDesc}${token}`);
    this.form.controls.description.markAsDirty();
  }

  removeFileByAttachmentId(id: string): void {
    this.localFileMap.delete(id);
    const match = /^file-(\d+)$/.exec(id);
    if (match) {
      const idx = parseInt(match[1], 10);
      if (Number.isInteger(idx) && idx >= 0 && idx < this.selectedFiles().length) {
        this.removeFile(idx);
        return;
      }
    }
  }

  removeFile(index: number): void {
    const tempId = `file-${index}`;
    this.localFileMap.delete(tempId);
    this.selectedFiles.update(current => current.filter((_, i) => i !== index));
  }

  getAttachmentIcon(type: string): string {
    if (type?.includes('image')) return 'mdi-file-image';
    if (type?.includes('pdf')) return 'mdi-file-pdf-box';
    if (type?.includes('json') || type?.includes('code') || type?.includes('text')) return 'mdi-file-code-outline';
    return 'mdi-paperclip';
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes < 1024) return `${bytes || 0} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alert.error('Please complete the type, title, and description.');
      return;
    }

    const requesterUserId = `${this.auth.user$()?.identityProfile?.id ?? ''}`.trim();
    if (!requesterUserId) {
      this.alert.error('Unable to determine the current user.');
      return;
    }

    const rawDescription = this.form.controls.description.value.trim();

    const payload: CreateSupportCaseDto = {
      requesterUserId,
      category: this.form.controls.type.value,
      priority: SupportCasePriority.MEDIUM,
      source: SupportCaseSource.WEB,
      title: this.form.controls.title.value.trim(),
      description: rawDescription,
    };

    this.loading = true;
    const filesToUpload = this.selectedFiles();

    this.api.submitFeedback(payload).pipe(
      switchMap(createdCase => {
        if (!filesToUpload.length) {
          return of(createdCase);
        }

        // Upload attachments and replace temporary attachment:file-N tokens in description if needed
        return forkJoin(
          filesToUpload.map((file, idx) =>
            this.api.uploadAttachment(createdCase.id, file).pipe(
              map(att => ({ idx, att })),
              catchError(err => {
                console.error('Failed to upload file:', file.name, err);
                return of({ idx, att: null as SupportCaseAttachment | null });
              })
            )
          )
        ).pipe(
          switchMap(results => {
            let updatedDescription = createdCase.description;
            let descriptionChanged = false;

            results.forEach(res => {
              if (res.att) {
                const tempToken = `attachment:file-${res.idx}`;
                if (updatedDescription.includes(tempToken)) {
                  updatedDescription = updatedDescription.replace(
                    new RegExp(tempToken, 'g'),
                    `attachment:${res.att.id}`
                  );
                  descriptionChanged = true;
                }
              }
            });

            if (descriptionChanged) {
              return this.api.update(createdCase.id, {
                supportCase: {
                  ...createdCase,
                  description: updatedDescription,
                },
                updateMask: { paths: ['description'] },
              });
            }
            return of(createdCase);
          })
        );
      })
    ).subscribe({
      next: () => {
        this.loading = false;
        this.alert.success('Feedback submitted successfully.');
        this.selectedFiles.set([]);
        this.form.reset({
          type: SupportCaseCategory.SUPPORT,
          title: '',
          description: '',
        });
        this.submitted.emit();
      },
      error: () => {
        this.loading = false;
        this.alert.error('Unable to submit feedback.');
      },
    });
  }

  setType(value: string): void {
    const parsed = Number(value);
    if (parsed === SupportCaseCategory.BUG || parsed === SupportCaseCategory.SUPPORT || parsed === SupportCaseCategory.FEATURE_REQUEST) {
      this.form.controls.type.setValue(parsed);
    }
  }
}
